import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import Logo from '../../../assets/images/media/logo_branco.png';
import { Ocorrencia, TimeSheetObject } from '../../../models/TimeSheet';
import { TimeSheetService } from '../../../services/TimeSheet.service';
import { hideSpinner, showSpinner } from '../../../store/slicers/globalSpinner.slicer';
import { checkResponseStatus } from '../../../utils/api/response';
import { formatDateWithBars } from '../../../utils/utils';
import { TableCellBody, TableCellHeader } from './styles';

interface Props {
  item: any;
  onClose: () => void;
}

export const TimeSheetDetailsDialog = ({ item, onClose }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const componentRef = React.useRef();

  const [timeSheetDetails, setTimeSheetDetails] = useState<TimeSheetObject | null>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Espelho Ponto - ${item.primeiro_dia_do_mes?.split(' ')[0]?.replaceAll('-', '/')}`
  });

  const fetchTimeSheetDetails = async () => {
    dispatch(showSpinner());
    const data = item.primeiro_dia_do_mes?.split(' ')[0]?.replaceAll('-', '/');

    try {
      const response = await TimeSheetService.getTimeSheetDetails(data);
      if (response && checkResponseStatus(response)) {
        setTimeSheetDetails(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(hideSpinner());
    }
  };

  useEffect(() => {
    if (item?.primeiro_dia_do_mes) {
      fetchTimeSheetDetails();
    }
  }, [item]);

  if (!timeSheetDetails) return null;

  const sortAndDistributeOcorrencias = (ocorrencias: Ocorrencia[]) => {
    const sortedOcorrencias = ocorrencias.sort(
      (a, b) => new Date(a.datainicioocorrencia).getTime() - new Date(b.datainicioocorrencia).getTime()
    );
    const columns: Ocorrencia[][] = [[], [], []];

    sortedOcorrencias.forEach((ocorrencia, index) => {
      columns[index % 2].push(ocorrencia);
    });

    return columns;
  };

  const ocorrenciaColumns = timeSheetDetails.dados_ocorrencias
    ? sortAndDistributeOcorrencias(timeSheetDetails.dados_ocorrencias)
    : [[], [], []];

  return (
    <Dialog open={!!timeSheetDetails} onClose={onClose} fullScreen={isMobile} fullWidth={true} maxWidth="lg" scroll="paper">
      <DialogTitle variant="h5" color="primary">
        Espelho do Ponto
      </DialogTitle>
      <Divider />
      <DialogContent ref={componentRef} sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: 'solid 2px #000',
            p: 0,
            minWidth: '700px'
          }}
        >
          <Box sx={{ display: 'flex', borderBottom: 'solid 2px #000' }}>
            <Box justifyContent="center" alignItems="center" display="flex">
              <img
                src={Logo}
                alt="Logo"
                style={{ maxWidth: '200px', height: 'auto', objectFit: 'cover', alignSelf: 'center', filter: 'contrast(800%)' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, padding: 1, justifyContent: 'space-evenly' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4">Espelho do Ponto</Typography>
                <Typography variant="h6">
                  <strong>Empregador: </strong> {timeSheetDetails?.cabecalho_empresa.razaosocial}
                </Typography>
                <Typography variant="h6">
                  <strong>Endereço: </strong>
                  {timeSheetDetails?.cabecalho_empresa.rua}, {timeSheetDetails?.cabecalho_empresa.nrorua}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h5">
                  Período De {formatDateWithBars(String(timeSheetDetails.cabecalho_empresa.periodo_ini.split(' ')?.[0]))} -{' '}
                  {formatDateWithBars(String(timeSheetDetails.cabecalho_empresa.periodo_fim.split(' ')?.[0]))}
                </Typography>
                <Typography variant="h6">
                  <strong>CNPJ: </strong> {timeSheetDetails?.cabecalho_empresa.inscricao}
                </Typography>
                <Typography variant="h6">
                  <strong>Cidade: </strong>
                  {timeSheetDetails?.cabecalho_empresa.cidade} - {timeSheetDetails?.cabecalho_empresa.uf}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', borderBottom: 'solid 2px #000', px: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6">
                <strong>Empregado: </strong>
                {timeSheetDetails?.cabecalho_funcionario.empresa} - {timeSheetDetails?.cabecalho_funcionario.nome}
              </Typography>
              <Typography variant="h6">
                <strong>Cargo: </strong>
                {timeSheetDetails?.cabecalho_funcionario.descricaocargo}
              </Typography>
              <Typography variant="h6">
                <strong>Centro de Custo Nível 1: </strong>
                {timeSheetDetails?.cabecalho_funcionario.descricaocentrocusto1}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                <strong>Data Admissão: </strong>
                {formatDateWithBars(timeSheetDetails?.cabecalho_funcionario.dataadmissao.split(' ')?.[0])}
              </Typography>
              <Typography variant="h6">
                <strong>Tipo de Salário: </strong>
                {timeSheetDetails?.cabecalho_funcionario.tipo_salario_descricao}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
            <Typography variant="h6">
              <strong>Horários: </strong>
            </Typography>
            <Grid container>
              {timeSheetDetails?.dados_horario?.map((horario, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Typography variant="body2">
                    {horario.horario} - {horario.descricao}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCellHeader>Data</TableCellHeader>
                <TableCellHeader>Dia</TableCellHeader>
                <TableCellHeader>Hor</TableCellHeader>
                <TableCellHeader>Marcações</TableCellHeader>
                <TableCellHeader sx={{ textAlign: 'center' }}>HE 50% D</TableCellHeader>
                <TableCellHeader sx={{ textAlign: 'center' }}>HE 50% N</TableCellHeader>
                <TableCellHeader sx={{ textAlign: 'center' }}>HE 100% D</TableCellHeader>
                <TableCellHeader sx={{ textAlign: 'center' }}>HE 100% N</TableCellHeader>
                <TableCellHeader sx={{ textAlign: 'center' }}>Faltas</TableCellHeader>
                <TableCellHeader sx={{ textAlign: 'center' }}>Afastamento</TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSheetDetails?.dados_marcacao?.map((dia, index) => (
                <TableRow key={index} sx={{ p: 0 }}>
                  <TableCellBody>{dia.dia_mes}</TableCellBody>
                  <TableCellBody>{dia.dia_semana}</TableCellBody>
                  <TableCellBody>{dia.horario}</TableCellBody>
                  <TableCellBody>{dia.marcacao}</TableCellBody>
                  <TableCellBody sx={{ textAlign: 'center' }}>{dia.hr_ext_50_dia}</TableCellBody>
                  <TableCellBody sx={{ textAlign: 'center' }}>{dia.hr_ext_50_noite}</TableCellBody>
                  <TableCellBody sx={{ textAlign: 'center' }}>{dia.hr_ext_100_dia}</TableCellBody>
                  <TableCellBody sx={{ textAlign: 'center' }}>{dia.hr_ext_100_noite}</TableCellBody>
                  <TableCellBody sx={{ textAlign: 'center' }}>{dia.hr_faltas}</TableCellBody>
                  <TableCellBody sx={{ textAlign: 'center' }}>{dia.hr_afast}</TableCellBody>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {ocorrenciaColumns && ocorrenciaColumns?.length > 0 && (
            <Grid container sx={{ px: 1, borderBottom: 'solid 2px #000' }}>
              {ocorrenciaColumns?.map((column, columnIndex) => (
                <Grid item xs={6} key={columnIndex}>
                  {column.map((item, index) => (
                    <Typography variant="body2" key={index}>
                      {item.texto}
                    </Typography>
                  ))}
                </Grid>
              ))}
            </Grid>
          )}
          <Box sx={{ px: 1, borderBottom: 'solid 2px #000' }}>
            <Grid container>
              {timeSheetDetails?.dados_totalizadores?.map((item, index) => (
                <Grid item xs={6} key={index}>
                  <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', maxWidth: '200px' }}>
                    <Typography variant="body2">{item.desc_resm}:</Typography>
                    <Typography variant="body2">{item.minutos}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box sx={{ px: 1 }}>
            <Box
              display="flex"
              justifyContent="space-evenly"
              alignItems="center"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                pt: 2
              }}
            >
              <Box sx={{ alignItems: 'center' }}>
                <Typography variant="h6">_________________________________________</Typography>
                <Typography variant="subtitle2" textAlign="center">
                  <strong>{timeSheetDetails.cabecalho_funcionario.nome}</strong>
                </Typography>
              </Box>
              <Box sx={{ alignItems: 'center' }}>
                <Typography variant="h6">_________________________________________</Typography>
                <Typography variant="subtitle2" textAlign="center">
                  <strong>Chefia</strong>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ mx: 2, my: 1, position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1 }}>
        <Button startIcon={<CloseIcon />} variant="outlined" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={handlePrint}>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};
